// Trainer feature: gym-logging domain model (PERSONAL_TRAINER_PLAN.md Phase 0,
// D-GYM-1/2/4). Pure data + pure helpers only — safe to import from both the
// API (Lambda) and the web SPA.
//
// `gym_exercise` is a NEW event type, distinct from the existing `exercise`
// event (cardio/chat capture, exercise.ts) which is left untouched (D-GYM-1).

import { ANATOMICAL_MUSCLES, type AnatomicalMuscle } from "./muscleMap.js";

/** Muscle-group taxonomy used to tag catalog entries and denormalize onto
 *  every logged `gym_exercise` event (so history stays stable even if a
 *  catalog entry's muscles are edited later). */
export const MUSCLE_GROUPS = [
  "chest", "back", "shoulders", "biceps", "triceps", "forearms",
  "core", "glutes", "quads", "hamstrings", "calves", "hip-abductors",
  "neck", "hip-flexors", "adductors",
  "cardio", "full-body",
] as const;
export type MuscleGroup = (typeof MUSCLE_GROUPS)[number];

/** Guard: true when `value` is a valid MUSCLE_GROUPS member. */
export function isMuscleGroup(value: string): value is MuscleGroup {
  return (MUSCLE_GROUPS as readonly string[]).includes(value);
}

/** Coarse push/pull/legs/core/cardio buckets the Balance tab renders bars for
 *  (D-GYM-4). `full-body` is not a bucket key — it fans out into every bucket
 *  at reduced weight instead (see computeMuscleLoad). */
export const BALANCE_BUCKETS: Record<string, MuscleGroup[]> = {
  push:   ["chest", "shoulders", "triceps"],
  pull:   ["back", "biceps", "forearms"],
  legs:   ["quads", "hamstrings", "glutes", "calves", "hip-abductors", "adductors", "hip-flexors"],
  core:   ["core"],
  cardio: ["cardio"],
};
// "neck" is deliberately in NO bucket (D-5). Neck load still appears in the
// per-group `muscle` record and on the body map; keeping it out of the buckets
// is what stops computeBalanceCallouts nagging every user about untrained neck.
// "adductors" and "hip-flexors" join `legs` so a pre-split event tagged coarse
// "hip-abductors" and a post-split event tagged "adductors" contribute to the
// SAME bucket -- bucket totals are unchanged for every already-stored event (D-4).

/** Metric fields the log modal can render for an exercise, in canonical
 *  display order (D-GYM-2). Each catalog entry / user exercise declares which
 *  of these apply via its `metrics` array; the modal shows exactly those. */
export const GYM_METRICS = [
  "sets", "reps", "weight",        // strength: weight carries weightUnit
  "durationMin", "distanceMi",     // cardio
  "laps",                          // pool / track
  "resistanceLevel", "incline",    // machine cardio (bike/stair/treadmill)
] as const;
export type GymMetric = (typeof GYM_METRICS)[number];

/** Guard: true when `value` is a valid GYM_METRICS member. */
export function isGymMetric(value: string): value is GymMetric {
  return (GYM_METRICS as readonly string[]).includes(value);
}

export type WeightUnit = "lbs" | "kg";

/** Unit a logged distance was entered in. Absent === "mi" — every pre-existing
 *  gym_exercise event stays correct without migration.
 *  NOTE: the payload field is named `distanceMi` for historical reasons and is NOT
 *  always miles. This tag, not the field name, is authoritative (D-3). */
export const DISTANCE_UNITS = ["mi", "km", "m"] as const;
export type DistanceUnit = (typeof DISTANCE_UNITS)[number];
export function isDistanceUnit(v: string): v is DistanceUnit {
  return (DISTANCE_UNITS as readonly string[]).includes(v);
}

/** "2000 m" / "5.2 km" / "3.1 mi". Returns undefined when there is no distance.
 *  Absent distanceUnit === "mi" (D-1). */
export function formatGymDistance(
  p: Pick<GymExercisePayload, "distanceMi" | "distanceUnit">,
  opts: { space?: boolean } = {}
): string | undefined {
  if (p.distanceMi == null) return undefined;
  return `${p.distanceMi}${opts.space === false ? "" : " "}${p.distanceUnit ?? "mi"}`;
}

/** Which side of the body a logged set worked (D-LAT-1). Absent on a payload is
 *  identical to "both" — every pre-laterality event stays correct (D-LAT-2). */
export const EXERCISE_SIDES = ["left", "right", "both"] as const;
export type ExerciseSide = (typeof EXERCISE_SIDES)[number];
export function isExerciseSide(v: string): v is ExerciseSide {
  return (EXERCISE_SIDES as readonly string[]).includes(v);
}

/** Max originals sent to gym-equipment identify in one analyze call — the
 *  Bedrock vision/token budget, NOT a storage limit (H-6 / D-10). Unchanged. */
export const MAX_GYM_EQUIP_PHOTOS = 3;

/** Max photos retained on a UserGymExercise total (`photoKey` + `extraPhotoKeys`).
 *  Higher than the per-analyze cap so two household members can each merge their
 *  own 3 photos of a shared machine without hitting the limit (D-10). */
export const MAX_GYM_EQUIP_STORED_PHOTOS = 6;

/**
 * Per-patient "thing I do" — the mutable catalog backing the Log tab's button
 * grid (D-GYM-2/5/6). Stored as a DynamoDB item under the patient partition
 * (`keys.gymExerciseSk`), NOT as an event — logging an exercise creates a
 * separate `gym_exercise` event and updates `lastValues`/`lastLoggedAt` here.
 */
export interface UserGymExercise {
  /** uuid, stable identity used by gym_exercise events and the program generator. */
  userExerciseId: string;
  /** Set when matched to a shared/src/gymCatalog.ts entry; undefined = custom. */
  catalogId?: string;
  /** Provenance only: the community registry id this snapshot was copied from. Never a live link. */
  communityId?: string;
  /** Display name (copied from the catalog match or entered for a custom exercise). */
  name: string;
  equipmentType: "machine" | "free-weight" | "bodyweight" | "cardio" | "cable" | "other";
  /** Copied from the catalog at creation time; user-editable, especially for custom entries. */
  primaryMuscles: MuscleGroup[];
  secondaryMuscles: MuscleGroup[];
  /** Which GYM_METRICS fields the log modal shows for this exercise. */
  metrics: GymMetric[];
  /** Free-text gym/location label, e.g. "Planet Fitness" or "Home" (D-GYM-6). */
  gymLabel?: string;
  /** Manufacturer/brand, e.g. "Life Fitness" — read off a nameplate during photo
   *  identify or entered manually; used to look up usage info for the machine. */
  brand?: string;
  /** URL to manufacturer usage info or a how-to-use video, e.g. from a QR code
   *  on the machine (read during photo identify) or entered manually. */
  infoUrl?: string;
  /** Specific anatomical muscle names for the primary muscles worked, e.g.
   *  "Anterior Deltoids (Front Shoulders)" — read off the photo during identify,
   *  in addition to (not instead of) the coarse `primaryMuscles` taxonomy tags
   *  the rest of the app (Balance tab, muscle load) is built on. */
  primaryMuscleDetail?: string[];
  /** Specific anatomical muscle names for the secondary muscles worked — see primaryMuscleDetail. */
  secondaryMuscleDetail?: string[];
  /** Fine-grained anatomical muscles this targets (MUSCLE_BODY_MAP_PLAN D-BM-3).
   *  Layered on top of — never instead of — the coarse `primaryMuscles` tags the
   *  Balance bars and bucket math run on. Optional: entries/events without these
   *  fall back to shading every member of their coarse group (D-BM-6). */
  primaryMuscleIds?: AnatomicalMuscle[];
  /** See primaryMuscleIds. */
  secondaryMuscleIds?: AnatomicalMuscle[];
  /** S3 key of the user's own machine photo, if captured via Phase 3. */
  photoKey?: string;
  /** S3 key of the cropped thumb used as the grid button image. */
  thumbKey?: string;
  /** Additional original S3 keys (not including photoKey). Length 0–5 (D-10). */
  extraPhotoKeys?: string[];
  /** Catalog silhouette path when available (D-GYM-7); preferred over photoKey
   *  once a specific icon exists for a catalog-matched exercise. */
  iconRef?: string;
  /** Prefill values for the log modal (D-GYM-2), updated on every log. */
  lastValues?: Partial<Record<GymMetric, number>> & {
    weightUnit?: WeightUnit;
    distanceUnit?: DistanceUnit;
  };
  /** ISO-8601, last time this exercise was logged. Drives grid sort (recent first). */
  lastLoggedAt?: string;
  /** Total times logged. Secondary sort / "favorites". */
  logCount?: number;
  /** Hidden from the Log grid and Equipment tab's active list when true. */
  archived?: boolean;
  /** True when this equipment works one side at a time (D-LAT-1) — a single-arm
   *  or single-leg machine/movement. Absent === false. Drives the log modal's
   *  left/right/both chips and the Left-vs-Right balance math. */
  unilateral?: boolean;
  createdAt: string;
}

/** Payload for a `gym_exercise` event — one logged set/session (D-GYM-1). All
 *  metric fields are optional at save time; a bare "did it" log is valid. */
export interface GymExercisePayload {
  userExerciseId: string;
  catalogId?: string;
  /** Denormalized for timeline display, independent of later renames. */
  name: string;
  /** ISO-8601 with offset. */
  performedAt: string;
  sets?: number;
  reps?: number;
  weight?: number;
  weightUnit?: WeightUnit;
  durationMin?: number;
  distanceMi?: number;
  distanceUnit?: DistanceUnit;
  laps?: number;
  resistanceLevel?: number;
  incline?: number;
  /** Denormalized at log time so history stays stable if the exercise's
   *  muscle tags are edited later (custom-exercise muscles are user-editable). */
  primaryMuscles: MuscleGroup[];
  secondaryMuscles: MuscleGroup[];
  /** Fine-grained anatomical muscles this targets (MUSCLE_BODY_MAP_PLAN D-BM-3).
   *  Layered on top of — never instead of — the coarse `primaryMuscles` tags the
   *  Balance bars and bucket math run on. Optional: entries/events without these
   *  fall back to shading every member of their coarse group (D-BM-6). */
  primaryMuscleIds?: AnatomicalMuscle[];
  /** See primaryMuscleIds. */
  secondaryMuscleIds?: AnatomicalMuscle[];
  gymLabel?: string;
  /** Which patient's partition holds the UserGymExercise definition this was
   *  logged against (D-HH-4). Absent === the logging patient owns it. Denormalized
   *  so confirm-time and edit paths can find the definition without re-resolving. */
  ownerPatientId?: string;
  /** Which side of the body this set worked (D-LAT-1/2). Only meaningful for
   *  `unilateral` equipment; absent is treated identically to "both", so every
   *  pre-laterality event stays correct. */
  side?: ExerciseSide;
  notes?: string;
  /** Muscle-mapping / scoring schema this log was saved under. Absent === 1
   *  (pre-taxonomy-correction). Stamped with `TRAINING_SCORING_VERSION` at save
   *  time; history is never rewritten. A reviewable historical-attribution
   *  correction UI stays a Stage 4 task. */
  mappingVersion?: number;
}

// ---- Household equipment sharing (TRAINER_HOUSEHOLD_AND_LATERALITY_PLAN.md D-HH-3/4/5) ----

/** A UserGymExercise as returned by GET /gym/exercises, resolved for ONE viewing patient
 *  (D-HH-3/4): the definition comes from `ownerPatientId`'s partition, while
 *  lastValues/lastLoggedAt/logCount/hidden are the VIEWER's own usage. Never stored. */
export interface GymExerciseView extends UserGymExercise {
  ownerPatientId: string;
  /** Owner's first name; undefined when the viewer owns it. */
  ownerLabel?: string;
  /** Owner's Patient.color, for the grid's owner dot; undefined when the viewer owns it. */
  ownerColor?: string;
  isOwn: boolean;
  /** Per-viewer hide (D-HH-6). Distinct from `archived`, which is owner-level. */
  hidden?: boolean;
  /** The viewer's last logged side for this (unilateral) equipment — prefills the
   *  log modal's left/right/both chips (D-LAT-2). From their own usage row only. */
  lastSide?: ExerciseSide;
}

/** One GYM_USAGE# row: the viewer's own usage of a (possibly shared) definition. */
export interface GymUsageRecord {
  userExerciseId: string;
  lastValues?: UserGymExercise["lastValues"];
  lastLoggedAt?: string;
  logCount?: number;
  hidden?: boolean;
  /** Last side logged, for the log modal's chip prefill (D-LAT-2). */
  lastSide?: ExerciseSide;
}

/**
 * Builds the per-viewer Log-grid view from household definitions + the viewer's usage rows
 * (D-HH-3/4/5). Rules, in order:
 *  1. Every definition is tagged with its ownerPatientId and isOwn.
 *  2. lastValues/lastLoggedAt/logCount come from the viewer's usage row when one exists.
 *  3. On a usage miss, they come from the definition's LEGACY inline fields ONLY when the
 *     viewer owns the definition (D-HH-5 lazy migration); otherwise they are cleared —
 *     showing another patient's prefill would be wrong, not merely stale.
 *  4. Sorted by the viewer's own lastLoggedAt desc, then logCount desc, matching
 *     listUserGymExercises' existing order.
 */
export function buildGymExerciseViews(
  viewerPatientId: string,
  defs: { patientId: string; exercise: UserGymExercise }[],
  usage: GymUsageRecord[],
  ownerMeta?: Map<string, { label: string; color: string }>
): GymExerciseView[] {
  const usageById = new Map(usage.map((u) => [u.userExerciseId, u]));
  const views = defs.map(({ patientId, exercise }) => {
    const isOwn = patientId === viewerPatientId;
    const u = usageById.get(exercise.userExerciseId);
    // D-HH-4/5: the viewer's own usage row wins; on a miss the legacy inline fields
    // count as the OWNER's usage, so they survive only for the owner.
    const lastValues = u ? u.lastValues : isOwn ? exercise.lastValues : undefined;
    const lastLoggedAt = u ? u.lastLoggedAt : isOwn ? exercise.lastLoggedAt : undefined;
    const logCount = u ? u.logCount : isOwn ? exercise.logCount : undefined;
    const meta = isOwn ? undefined : ownerMeta?.get(patientId);
    const view: GymExerciseView = {
      ...exercise,
      lastValues,
      lastLoggedAt,
      logCount,
      ownerPatientId: patientId,
      isOwn,
      ...(meta ? { ownerLabel: meta.label, ownerColor: meta.color } : {}),
      ...(u?.hidden !== undefined ? { hidden: u.hidden } : {}),
      ...(u?.lastSide !== undefined ? { lastSide: u.lastSide } : {}),
    };
    return view;
  });
  return views.sort((a, b) => {
    const at = a.lastLoggedAt ?? "";
    const bt = b.lastLoggedAt ?? "";
    if (at !== bt) return bt.localeCompare(at);
    return (b.logCount ?? 0) - (a.logCount ?? 0);
  });
}

/** One exercise slot within a generated `gym_program` day (Phase 6). References
 *  the user's own equipment — never invents equipment the patient doesn't have. */
export interface GymProgramExercise {
  /** Set when the exercise is on the user's grid; undefined for a bodyweight
   *  movement that needs no catalog/equipment entry. */
  userExerciseId?: string;
  catalogId?: string;
  name: string;
  sets?: string;
  reps?: string;
  startingWeightNote?: string;
  restNote?: string;
}

export type GymProgramDay = { day: string; exercises: GymProgramExercise[] };

/** Snapshot of the intake the program was generated from, embedded in the
 *  event so the generation inputs stay reconstructable (Phase 6, D-GYM-8). */
export interface GymProgramIntake {
  activityLevel: "sedentary" | "light" | "moderate" | "active";
  goals: string[];
  daysPerWeek: number;
  sessionLengthMin?: number;
  limitations?: string;
  /** eventIds of recent `injury` events the user flagged as "still limits me". */
  activeInjuryEventIds?: string[];
}

/** Payload for a `gym_program` event — an AI-generated starter program built
 *  from the user's own equipment + profile + intake (Phase 6, D-GYM-8). Always
 *  `source: "ai_generated"` — educational suggestion, never a prescription
 *  (same posture as physicalTherapy.ts's D-PT-13 disclaimer). */
export interface GymProgramPayload {
  programTitle: string;
  days: GymProgramDay[];
  intake: GymProgramIntake;
  rationale?: string;
  progressionNote?: string;
  source: "ai_generated";
  archived?: boolean;
}

/** Per-muscle and per-bucket training load over a trailing window, used by the
 *  Balance tab (D-GYM-4). Counts are session counts, not sets/reps — this is a
 *  frequency-balance view, not a volume tracker. */
export interface MuscleLoad {
  /** Combined per-muscle load (musclePrimary + muscleSecondary) — what the bucket math sums over. */
  muscle: Record<MuscleGroup, number>;
  /** Per-muscle load from primary-muscle mentions only (weight 1.0). Balance tab bar split. */
  musclePrimary: Record<MuscleGroup, number>;
  /** Per-muscle load from secondary-muscle mentions only (weight 0.5). Balance tab bar split. */
  muscleSecondary: Record<MuscleGroup, number>;
  bucket: Record<string, number>;
  /** Total gym_exercise events considered (after the days-window filter). */
  sessionCount: number;
}

const PRIMARY_WEIGHT = 1.0;
const SECONDARY_WEIGHT = 0.5;
/** Weight `full-body` contributes to EVERY bucket, per event (not per muscle
 *  entry) — a full-body movement shouldn't look like it single-handedly
 *  balanced push/pull/legs the way training each group directly would. */
const FULL_BODY_BUCKET_WEIGHT = 0.5;

/**
 * Computes muscle- and bucket-level training load from a set of gym_exercise
 * payloads, filtered to the trailing `days` window ending at `now`. Pure
 * function — the Balance tab (client) and /gym/recommendations (server) both
 * call this so the math can never drift between the two.
 */
export function computeMuscleLoad(
  payloads: GymExercisePayload[],
  days: number,
  now: Date = new Date(),
): MuscleLoad {
  const cutoff = now.getTime() - days * 24 * 60 * 60 * 1000;
  const muscle = Object.fromEntries(MUSCLE_GROUPS.map((m) => [m, 0])) as Record<MuscleGroup, number>;
  const musclePrimary = Object.fromEntries(MUSCLE_GROUPS.map((m) => [m, 0])) as Record<MuscleGroup, number>;
  const muscleSecondary = Object.fromEntries(MUSCLE_GROUPS.map((m) => [m, 0])) as Record<MuscleGroup, number>;
  const bucket = Object.fromEntries(Object.keys(BALANCE_BUCKETS).map((b) => [b, 0])) as Record<string, number>;
  let sessionCount = 0;

  for (const payload of payloads) {
    const performedAt = new Date(payload.performedAt).getTime();
    if (Number.isNaN(performedAt) || performedAt < cutoff || performedAt > now.getTime()) continue;
    sessionCount++;

    let touchedFullBody = false;
    for (const m of payload.primaryMuscles) {
      muscle[m] = (muscle[m] ?? 0) + PRIMARY_WEIGHT;
      musclePrimary[m] = (musclePrimary[m] ?? 0) + PRIMARY_WEIGHT;
      if (m === "full-body") touchedFullBody = true;
    }
    for (const m of payload.secondaryMuscles) {
      muscle[m] = (muscle[m] ?? 0) + SECONDARY_WEIGHT;
      muscleSecondary[m] = (muscleSecondary[m] ?? 0) + SECONDARY_WEIGHT;
      if (m === "full-body") touchedFullBody = true;
    }

    for (const [bucketName, members] of Object.entries(BALANCE_BUCKETS)) {
      let add = 0;
      for (const m of payload.primaryMuscles) if (members.includes(m)) add += PRIMARY_WEIGHT;
      for (const m of payload.secondaryMuscles) if (members.includes(m)) add += SECONDARY_WEIGHT;
      if (touchedFullBody) add += FULL_BODY_BUCKET_WEIGHT;
      bucket[bucketName] += add;
    }
  }

  return { muscle, musclePrimary, muscleSecondary, bucket, sessionCount };
}

/** Minimal shape both `UserGymExercise` and `GymCatalogEntry` satisfy — kept
 *  structural (not imported from gymCatalog.ts) so this module has no
 *  dependency on the catalog and stays leaf-level/import-cycle-free. */
export interface MuscleTaggedExercise {
  id: string;
  name: string;
  primaryMuscles: MuscleGroup[];
  secondaryMuscles: MuscleGroup[];
}

/** One neglected-bucket callout for the Balance tab (D-GYM-4). */
export interface BalanceCallout {
  bucket: string;
  /** e.g. "You haven't trained legs in this window — consider adding one of:" */
  message: string;
  /** Matching exercises to suggest, own catalog first, reference catalog second. */
  suggestions: { source: "own" | "catalog"; id: string; name: string }[];
}

/** Minimum session count another bucket must reach before a zero-load bucket
 *  is called out — avoids flagging "neglected cardio" in a brand-new account
 *  where nothing has much load yet. */
const CALLOUT_MIN_OTHER_BUCKET_LOAD = 3;
const CALLOUT_MAX_SUGGESTIONS = 3;

const BUCKET_LABELS: Record<string, string> = {
  push: "push",
  pull: "pull",
  legs: "legs",
  core: "core",
  cardio: "cardio",
};

/**
 * Rule-based "neglected group" callouts (D-GYM-4): any bucket at zero load
 * while another bucket has at least `CALLOUT_MIN_OTHER_BUCKET_LOAD` sessions
 * gets a callout, with suggested exercises drawn from the user's own equipment
 * first, the reference catalog second. Pure — the web Balance tab and the
 * server (context for /gym/recommendations) both call this so the rule can
 * never drift between the two.
 */
export function computeBalanceCallouts(
  load: MuscleLoad,
  ownExercises: MuscleTaggedExercise[],
  catalogEntries: MuscleTaggedExercise[]
): BalanceCallout[] {
  const buckets = Object.entries(load.bucket);
  const maxOtherLoad = (bucketName: string) =>
    Math.max(0, ...buckets.filter(([b]) => b !== bucketName).map(([, v]) => v));

  const callouts: BalanceCallout[] = [];
  for (const [bucketName, value] of buckets) {
    if (value > 0) continue;
    if (maxOtherLoad(bucketName) < CALLOUT_MIN_OTHER_BUCKET_LOAD) continue;

    const members = BALANCE_BUCKETS[bucketName] ?? [];
    const matches = (pool: MuscleTaggedExercise[]) =>
      pool.filter((e) => e.primaryMuscles.some((m) => members.includes(m)));

    const ownMatches = matches(ownExercises).map((e) => ({ source: "own" as const, id: e.id, name: e.name }));
    const catalogMatches =
      ownMatches.length > 0
        ? []
        : matches(catalogEntries).map((e) => ({ source: "catalog" as const, id: e.id, name: e.name }));

    callouts.push({
      bucket: bucketName,
      message: `You haven't trained ${BUCKET_LABELS[bucketName] ?? bucketName} in this window — consider adding one of:`,
      suggestions: [...ownMatches, ...catalogMatches].slice(0, CALLOUT_MAX_SUGGESTIONS),
    });
  }
  return callouts;
}

/** One AI-suggested exercise from `/gym/recommendations` (D-GYM-4). `exerciseRef`
 *  is a `userExerciseId` when `refType` is "own" (the user already has this on
 *  their grid — the UI can log it directly) or a `gymCatalog.ts` id when "catalog"
 *  (a suggestion to add new equipment covering a neglected group). The model may
 *  only reference ids from those two lists — never invent one (same rule as
 *  D-PT-2 / D-GYM-3). */
export interface GymRecommendationItem {
  exerciseRef: string;
  refType: "own" | "catalog";
  reason: string;
  /** Conservative starting point, e.g. "2 sets x 10, light weight". */
  suggestedStart: string;
}

/** Cached result of the on-demand AI balance-recommendation call, stored in the
 *  per-patient `gymRecommendationSk()` row; regenerating overwrites it (D-GYM-4
 *  defaults). Educational suggestion, not medical advice (same posture as
 *  D-PT-13) — always render with a disclaimer. */
export interface GymRecommendationResult {
  summary: string;
  recommendations: GymRecommendationItem[];
  generatedAt: string;
  /** The window the assessment was generated from. Absent on pre-provenance rows. */
  generatedWindow?: { days: number; asOfIso: string; timezone: string; basis: "rolling-hours" };
  /** TRAINING_SCORING_VERSION at generation time. */
  scoringVersion?: number;
  /** Truncated sha256 over the generating inputs; recomputed on read to detect drift. */
  inputFingerprint?: string;
  /** Set on GET when inputs or scoring version drifted. Never auto-regenerate;
   *  never persist this flag. */
  stale?: boolean;
}

// ── Chat capture (Phase 7, D-GYM-1/2/5) ─────────────────────────────────────

/** Equipment types a chat-captured custom exercise can guess (mirrors
 *  UserGymExercise["equipmentType"] / GymCatalogEntry["equipmentType"]). */
export const GYM_EQUIPMENT_TYPES = ["machine", "free-weight", "bodyweight", "cardio", "cable", "other"] as const;

/**
 * Chat-capture tool for logging a gym exercise conversationally (e.g. "I did 3
 * sets of 10 on the leg press at 90 lbs"). The capture prompt injects the
 * patient's own gym exercise grid (name + id) into PATIENT CONTEXT so the model
 * can set `userExerciseId` when it matches one already on the grid; when nothing
 * matches, it omits `userExerciseId` and instead gives its best guess at
 * `equipmentType`/`primaryMuscles`/`secondaryMuscles`/`metrics` so the API can
 * create a new grid entry (D-GYM-5 — user-editable afterward from the Equipment
 * tab, same as a photo-identified machine). Never invents a `userExerciseId`
 * that wasn't in the injected list (same rule as D-PT-2 / D-GYM-3).
 */
export const RECORD_GYM_EXERCISE_TOOL = {
  name: "record_gym_exercise",
  description:
    "Record a strength-training set or cardio-machine session the user just did, e.g. 'I did 3 sets " +
    "of 10 on the leg press at 90 lbs' or 'I ran 20 minutes on the treadmill'. Check PATIENT'S GYM " +
    "EXERCISES in the PATIENT CONTEXT: if the exercise the user named matches one there, set " +
    "userExerciseId to its id and you don't need to guess muscles/metrics. If it doesn't match anything " +
    "on their grid, leave userExerciseId unset and instead give your best guess for equipmentType, " +
    "primaryMuscles, secondaryMuscles, and metrics so a new grid entry can be created for it — the user " +
    "can correct these later. Only include the metric fields the user actually stated.",
  input_schema: {
    type: "object",
    properties: {
      userExerciseId: {
        type: "string",
        description: "id from PATIENT'S GYM EXERCISES matching the exercise named, if any.",
      },
      name: {
        type: "string",
        description: "The exercise name as the user said it, e.g. 'leg press'.",
      },
      equipmentType: {
        type: "string",
        enum: GYM_EQUIPMENT_TYPES,
        description: "Best guess at the equipment type. Used only when userExerciseId is not set.",
      },
      primaryMuscles: {
        type: "array",
        items: { type: "string", enum: MUSCLE_GROUPS },
        description: "Best-guess primary muscles worked. Used only when userExerciseId is not set.",
      },
      secondaryMuscles: {
        type: "array",
        items: { type: "string", enum: MUSCLE_GROUPS },
        description: "Best-guess secondary muscles worked. Used only when userExerciseId is not set.",
      },
      primaryMuscleIds: {
        type: "array",
        items: { type: "string", enum: ANATOMICAL_MUSCLES },
        description:
          "Best-guess FINE-GRAINED anatomical muscles primarily worked (MUSCLE_BODY_MAP_PLAN.md), from the " +
          "same list as the coarse primaryMuscles groups above. Used only when userExerciseId is not set.",
      },
      secondaryMuscleIds: {
        type: "array",
        items: { type: "string", enum: ANATOMICAL_MUSCLES },
        description: "Best-guess fine-grained anatomical secondary muscles worked. Used only when userExerciseId is not set.",
      },
      metrics: {
        type: "array",
        items: { type: "string", enum: GYM_METRICS },
        description: "Which metric fields apply to this exercise. Used only when userExerciseId is not set.",
      },
      side: {
        type: "string",
        enum: EXERCISE_SIDES,
        description:
          "Which side of the body, ONLY for one-side-at-a-time movements and ONLY if the user says so. Omit otherwise.",
      },
      sets: { type: "number" },
      reps: { type: "number" },
      weight: { type: "number" },
      weightUnit: { type: "string", enum: ["lbs", "kg"] },
      durationMin: { type: "number", description: "Minutes, for cardio." },
      distanceMi: {
        type: "number",
        description: "Distance value for cardio; the unit is given by distanceUnit (default miles).",
      },
      distanceUnit: {
        type: "string",
        enum: DISTANCE_UNITS,
        description: "Unit the distance was given in. Default mi.",
      },
      laps: { type: "number", description: "For pool/track." },
      resistanceLevel: { type: "number", description: "For machine cardio (bike/stair climber)." },
      incline: { type: "number", description: "For treadmill, percent." },
      gymLabel: { type: "string", description: "Gym/location, e.g. 'Planet Fitness', only if the user mentions it." },
      notes: { type: "string" },
      performedAt: {
        type: "string",
        description: "ISO-8601 timestamp when it happened. Omit to use the current time.",
      },
    },
    required: ["name"],
  },
} as const;
